import { useDispatch, useStore } from "react-redux";

import { RootState } from "@store/store";
import { refreshApplicationSelected, setApplicationSelected } from "@store/slices/componentsSlice";
import { useLazyGetApplicationResumeByCompanyIdQuery } from "@services/api/applications";
import { useLazyGetTaxSystemQuery } from "@services/api/catalogs";
import { deepEqual, omit } from "@helpers/generals";
import { ApplicationMarketplaceResume, ApplicationResume } from "@interfaces/application";
import { TaxSystem } from "@interfaces/catalogs";

type AnyApplicationResume = ApplicationResume | ApplicationMarketplaceResume;

// Campos que cambian en cada consulta y no deben provocar una actualización del store
const VOLATILE_FIELDS = ["screen_time_hours", "total_time_hours", "client_total_time_hours"];

// Banderas que solo existen en el store (las calculan las secciones)
const LOCAL_FLAGS = [
    "dictamenCompletado",
    "company_docs_validated",
    "shareholder_docs_validated",
    "owners_docs_validated",
    "existsSDC",
    "visit_analysis_validated",
    "check_control_validated",
];

export const buildApplicationSelected = (
    resume: AnyApplicationResume,
    taxSystem: TaxSystem | undefined,
): AnyApplicationResume => ({
    ...resume,
    tax_system: {
        ...resume.tax_system,
        config_extra: taxSystem
            ? omit(taxSystem.config_extra, ["bpm_lateral_options", "bpm_lateral_options_v2"])
            : resume.tax_system.config_extra,
    },
});

const isSameApplicationData = (current: object, next: object) =>
    deepEqual(
        omit(current as Record<string, unknown>, [...VOLATILE_FIELDS, ...LOCAL_FLAGS]),
        omit(next as Record<string, unknown>, VOLATILE_FIELDS),
    );

export interface LoadApplicationSelectedOptions {
    companyId: number;
    companySource: string;
    /** Ignora el caché de RTK Query para el resumen (por defecto true) */
    forceResume?: boolean;
    /** Ignora el caché de RTK Query para el régimen fiscal (por defecto false) */
    forceTaxSystem?: boolean;
    /**
     * "refresh": conserva las banderas locales si es la misma solicitud y no actualiza si no hubo cambios.
     * "replace": reemplaza la solicitud seleccionada (reinicia banderas locales).
     */
    mode?: "refresh" | "replace";
    /** Si es false solo regresa la solicitud sin actualizar el store */
    updateStore?: boolean;
}

/**
 * Centraliza la consulta del resumen de la solicitud (vista loan_application_resume) junto con el
 * config_extra de su régimen fiscal, y su almacenamiento en `components.applicationSelected`.
 */
export function useLoadApplicationSelected() {
    const dispatch = useDispatch();
    const store = useStore<RootState>();

    const [triggerGetApplicationResume] = useLazyGetApplicationResumeByCompanyIdQuery();
    const [triggerGetTaxSystem] = useLazyGetTaxSystemQuery();

    const loadApplicationSelected = async ({
        companyId,
        companySource,
        forceResume = true,
        forceTaxSystem = false,
        mode = "refresh",
        updateStore = true,
    }: LoadApplicationSelectedOptions): Promise<AnyApplicationResume | undefined> => {
        const resume = await triggerGetApplicationResume(
            {
                companyId,
                params: new URLSearchParams([["company_source", companySource.toUpperCase()]]).toString(),
            },
            !forceResume,
        ).unwrap();

        if (!resume) return undefined;

        const taxSystem = await triggerGetTaxSystem(resume.tax_system.id, !forceTaxSystem).unwrap();
        const application = buildApplicationSelected(resume, taxSystem);

        if (!updateStore) return application;

        if (mode === "replace") {
            dispatch(setApplicationSelected(application));
            return application;
        }

        if (!isSameApplicationData(store.getState().components.applicationSelected, application)) {
            dispatch(refreshApplicationSelected(application));
        }

        return application;
    };

    return { loadApplicationSelected };
}
